<ng-container *vsLet="canUpdate$ | async as canUpdate">
  <ng-container *vsLet="canAdjust$ | async as canAdjust">
    <vs-app-page
      *ngIf="warranty$ | async as warranty"
      class="flex-grow"
      [config]="{
        title: warranty.policy?.title || '',
        description: '# ' + warranty.contractNumber || '',
        icon: 'folder-shield',
        wrapTitle: true,
      }"
    >
      <vs-breadcrumbs
        slot="breadcrumbs"
        [config]="{
          items: [{ label: 'Warranties', routerLink: '..' }, { label: warranty.contractNumber }],
        }"
      ></vs-breadcrumbs>

      <span class="inline-flex items-center justify-center min-w-fit self-center" slot="title-end" icon="folder-shield">
        <vs-badge
          [config]="{ displayStatusIcon: true }"
          [theme]="
            warranty.status === activated
              ? 'success'
              : warranty.status === cancelled || warranty.status === 'VOID'
                ? 'warn'
                : warranty.status === expired
                  ? 'danger'
                  : 'default'
          "
        >
          {{ getStatusLabel(warranty.status) | titlecase }}
        </vs-badge>
      </span>

      <span
        class="inline-flex items-center justify-center min-w-fit self-center"
        slot="title-end"
        *ngIf="warranty.frozen"
      >
        <vs-badge theme="info" [config]="{ iconStart: 'snowflake-01' }">
          {{ warranty.externalPaymentHold ? 'Frozen · Awaiting seller receipts' : 'Frozen' }}
        </vs-badge>
      </span>

      <span
        class="inline-flex items-center justify-center min-w-fit self-center"
        slot="title-end"
        *ngIf="(warranty.status === 'CANCELLED' || warranty.status === 'VOID') && warranty.cancellationReason"
      >
        <vs-icon size="20" name="arrow-right" class="text-primary-600"></vs-icon>
        <vs-badge class="max-w-full py-2">
          {{ warranty.cancellationReason }}
        </vs-badge>
      </span>

      <div
        slot="header-end"
        class="flex flex-wrap md:flex-nowrap justify-end items-center gap-2 max-w-full md:whitespace-nowrap"
      >
        <button
          *ngIf="canShowDeclaration$ | async"
          vs-button
          appearance="outline"
          [disabled]="generatingDeclaration"
          (click)="downloadDeclaration(warranty)"
        >
          <vs-icon *ngIf="!generatingDeclaration" name="file-06" size="16"></vs-icon>
          <vs-icon *ngIf="generatingDeclaration" name="loading-01" size="16" class="animate-spin"></vs-icon>
          {{ generatingDeclaration ? 'Generating...' : 'View Registration' }}
        </button>

        <ng-container
          *ngIf="(warranty.status === registered || warranty.status === activated) && (canCancelWarranty$ | async)"
        >
          <button vs-button theme="danger" appearance="outline" (click)="cancelWarrantyDialog.open()">
            Cancel warranty
          </button>

          <ws-cancel-warranty-dialog
            #cancelWarrantyDialog
            [warranty]="warranty"
            (cancelled)="updateCancelled(warranty)"
            (cancelFailed)="refresh(warranty.id)"
          ></ws-cancel-warranty-dialog>
        </ng-container>

        <ng-container *ngIf="warranty.status !== voided && (canCancelWarranty$ | async)">
          <button vs-button theme="danger" appearance="outline" (click)="voidWarrantyDialog.open()">
            VOID warranty
          </button>

          <ws-void-warranty-dialog
            #voidWarrantyDialog
            [warranty]="warranty"
            (voided)="updateVoided(warranty)"
            (voidFailed)="refresh(warranty.id)"
          >
          </ws-void-warranty-dialog>
        </ng-container>

        <button
          *ngIf="warranty.status === draft && (canFinalize$ | async)"
          vs-button
          appearance="outline"
          (click)="registerDialog.open()"
        >
          Register
        </button>
        <button
          *ngIf="warranty.status === draft && canUpdate"
          vs-button
          appearance="outline"
          (click)="deleteDialog.open()"
        >
          Delete
        </button>

        <ng-container *ngIf="canAdjust">
          <button
            #trigger="cdkOverlayOrigin"
            vs-button
            cdkOverlayOrigin
            appearance="outline"
            (click)="overlayOpen = !overlayOpen; $event.stopImmediatePropagation()"
            [vsTooltip]="'Updating the start date will automatically adjust the expiry date'"
            tooltipStyle="light"
          >
            Edit Start Date
          </button>
          <ng-template
            #overlay="cdkConnectedOverlay"
            cdkConnectedOverlay
            [cdkConnectedOverlayOrigin]="trigger"
            [cdkConnectedOverlayOpen]="overlayOpen"
            (overlayOutsideClick)="overlayOpen ? (overlayOpen = false) : ''"
          >
            <vs-card>
              <vs-date-picker
                [clearSelectionButton]="false"
                [value]="warranty?.termStart"
                (selectionChanged)="updateTermStart(warranty, $any($event)); overlayOpen = false"
              ></vs-date-picker>
            </vs-card>
          </ng-template>
        </ng-container>
      </div>

      <vs-tabs>
        <vs-tab [config]="{ label: 'Overview' }">
          <div class="flex sm:flex-col flex-wrap gap-4 w-full">
            <ws-warranty-overview-card
              class="w-full"
              [warranty]="warranty"
              [canUpdate]="canUpdate ?? false"
              (refresh)="refresh(warranty.id)"
            ></ws-warranty-overview-card>

            <ng-container *vsLet="canSeeFees$ | async as canSeeFees">
              <vs-card *ngIf="coverageData$ | async as data" class="overflow-hidden">
                <vs-card-header
                  [config]="{
                    title: canSeeFees ? 'Coverages, Fees and Retail' : 'Coverages and Retail',
                    subtitle: canSeeFees
                      ? 'Detailed breakdown of the contract\'s included coverages, fees and retail price.'
                      : 'Detailed breakdown of the contract\'s included coverages and retail price.',
                  }"
                >
                  <a
                    vs-icon-button
                    slot="end"
                    target="_blank"
                    appearance="clear"
                    [href]="data.downloadUrl"
                    [download]="warranty.contractNumber + '.csv'"
                  >
                    <vs-icon name="download-01" size="20"></vs-icon>
                  </a>

                  <ng-container *ngIf="canSeeFees" slot="end">
                    <button vs-icon-button class="ml-2" appearance="clear" [vsMenuTriggerFor]="options_menu">
                      <vs-icon name="dots-vertical" size="20"></vs-icon>
                    </button>

                    <ng-template #options_menu>
                      <vs-menu>
                        <button vs-menu-item keepOpen (triggered)="hideFees$.next(!hideFees$.value)">
                          <input
                            vsInput
                            slot="start"
                            type="checkbox"
                            class="pointer-events-none"
                            [checked]="!hideFees$.value"
                          />
                          <span>Show Fees</span>
                        </button>
                      </vs-menu>
                    </ng-template>
                  </ng-container>
                </vs-card-header>

                <ng-container>
                  <div class="overflow-x-auto scrollbar-thin">
                    <div class="inline-block min-w-full">
                      <div
                        class="grid grid-cols-[repeat(5,minmax(128px,1fr))] bg-base-white"
                        [style.grid-template-columns]="'repeat(' + data.columns + ',minmax(128px,1fr))'"
                      >
                        <!-- Base Columns -->
                        <ng-container>
                          <div
                            class="sticky z-10 left-0 text-xs font-medium text-gray-600 px-4 py-2 border-b border-r bg-base-white"
                          >
                            Coverage
                          </div>

                          <div
                            class="text-xs font-medium text-gray-600 px-4 py-2 border-b border-r border-l bg-base-white"
                          >
                            Included
                          </div>

                          <div
                            class="text-xs font-medium text-gray-600 px-4 py-2 border-b border-r bg-base-white text-right"
                          >
                            Deductible
                          </div>

                          <div
                            class="text-xs font-medium text-gray-600 px-4 py-2 border-b border-r bg-base-white text-right"
                          >
                            Liability Limit
                          </div>
                        </ng-container>

                        <ng-container *ngIf="data.columns > 5">
                          <!-- Fee Columns -->
                          <ng-container *ngFor="let fee of data.feeTitles">
                            <div
                              class="text-xs font-medium text-gray-600 px-4 py-2 border-b border-r bg-base-white text-right"
                            >
                              {{ fee }}
                            </div>
                          </ng-container>

                          <div
                            class="text-xs font-medium text-gray-600 px-4 py-2 border-b border-r bg-base-white text-right"
                          >
                            Cost
                          </div>
                        </ng-container>

                        <!-- Retail Column -->
                        <div
                          class="sticky right-0 z-10 border-l text-xs font-medium text-gray-600 px-4 py-2 border-b bg-base-white text-right"
                        >
                          Retail
                        </div>

                        <!-- Coverages -->
                        <ng-container *ngFor="let cov of data.coverages; let odd_row = odd">
                          <div
                            class="sticky z-10 left-0 text-sm font-medium text-gray-900 px-4 py-2 truncate overflow-hidden border-r border-b"
                            [class.bg-base-white]="odd_row"
                            [class.bg-gray-50]="!odd_row"
                            tooltipOpenDelay="250"
                            tooltipStyle="light"
                            [vsTooltip]="cov.coverage?.title ?? undefined"
                          >
                            {{ cov.coverage?.title ?? 'Unknown' }}
                          </div>

                          <div
                            class="text-sm text-gray-900 px-4 py-2 truncate overflow-hidden border-r border-l border-b"
                            tooltipOpenDelay="250"
                            tooltipStyle="light"
                            [vsTooltip]="
                              cov.included !== 'BASE' && cov.included !== 'NOT_APPLICABLE' ? cov.included : undefined
                            "
                            [class.bg-base-white]="odd_row"
                            [class.bg-gray-50]="!odd_row"
                          >
                            <ng-container [ngSwitch]="cov.included">
                              <vs-badge *ngSwitchCase="'BASE'" class="max-w-full" theme="primary">
                                <span>Base</span>
                              </vs-badge>
                              <vs-badge *ngSwitchCase="'NOT_APPLICABLE'" class="max-w-full" theme="danger">
                                <span>Not Included</span>
                              </vs-badge>
                              <vs-badge *ngSwitchDefault theme="info" class="max-w-full">
                                <span class="truncate">{{ cov.included }}</span>
                              </vs-badge>
                            </ng-container>
                          </div>

                          <div
                            class="text-sm text-gray-900 px-4 py-2 truncate overflow-hidden border-r border-b text-right"
                            [class.bg-base-white]="odd_row"
                            [class.bg-gray-50]="!odd_row"
                          >
                            {{ cov.deductible / 100 | currency: 'USD' : 'symbol' : '1.2-2' }}
                          </div>

                          <div
                            class="text-sm text-gray-900 px-4 py-2 truncate overflow-hidden border-r border-b text-right"
                            [class.bg-base-white]="odd_row"
                            [class.bg-gray-50]="!odd_row"
                            tooltipOpenDelay="250"
                            tooltipStyle="light"
                            [vsTooltip]="{ title: 'Liability Limits', subtitle: cov.limitsTooltip }"
                          >
                            {{ ($any(cov.limit)?.value ?? 0) / 100 | currency: 'USD' : 'symbol' : '1.2-2' }}
                          </div>

                          <ng-container *ngIf="data.columns > 5">
                            <ng-container *ngFor="let fee of cov.fees">
                              <div
                                class="text-sm text-gray-900 px-4 py-2 truncate overflow-hidden border-r border-b text-right"
                                [class.bg-base-white]="odd_row"
                                [class.bg-gray-50]="!odd_row"
                              >
                                {{ fee / 100 | currency: 'USD' : 'symbol' : '1.2-2' }}
                              </div>
                            </ng-container>

                            <div
                              class="text-sm text-gray-900 px-4 py-2 truncate overflow-hidden border-r border-b text-right"
                              [class.bg-base-white]="odd_row"
                              [class.bg-gray-50]="!odd_row"
                            >
                              {{ cov.cost / 100 | currency: 'USD' : 'symbol' : '1.2-2' }}
                            </div>
                          </ng-container>

                          <div
                            class="sticky right-0 z-10 border-l text-sm text-gray-900 px-4 py-2 truncate overflow-hidden border-b text-right"
                            [class.bg-base-white]="odd_row"
                            [class.bg-gray-50]="!odd_row"
                          >
                            {{ cov.price / 100 | currency: 'USD' : 'symbol' : '1.2-2' }}
                          </div>
                        </ng-container>

                        <ng-container *ngIf="data.markup > 0">
                          <div
                            class="sticky z-10 left-0 text-sm font-medium text-gray-900 px-4 py-2 truncate overflow-hidden border-r border-b bg-base-white"
                          >
                            Distributor Markup
                          </div>

                          <div class="border-x border-b bg-base-white col-span-3"></div>

                          <ng-container *ngIf="data.columns > 5">
                            <ng-container *ngFor="let fee of data.feeTitles">
                              <div
                                class="text-sm text-gray-900 px-4 py-2 truncate overflow-hidden border-r border-b text-right bg-base-white"
                              >
                                -
                              </div>
                            </ng-container>

                            <div
                              class="text-sm font-medium text-gray-900 px-4 py-2 truncate overflow-hidden border-r border-b text-right bg-base-white"
                            >
                              {{ data.markup / 100 | currency: 'USD' : 'symbol' : '1.2-2' }}
                            </div>
                          </ng-container>

                          <div
                            class="sticky right-0 z-10 border-l text-sm font-medium text-gray-900 px-4 py-2 truncate overflow-hidden border-b text-right bg-base-white"
                          >
                            {{ data.markup / 100 | currency: 'USD' : 'symbol' : '1.2-2' }}
                          </div>
                        </ng-container>

                        <!-- Totals -->
                        <ng-container>
                          <div
                            class="sticky z-10 left-0 text-sm font-medium text-gray-900 px-4 py-2 truncate overflow-hidden border-t border-r bg-base-white"
                          >
                            Total
                          </div>

                          <div class="border-x border-t bg-base-white col-span-3"></div>

                          <ng-container *ngIf="data.columns > 5">
                            <div
                              *ngFor="let fee of data.feeTitles; let index = index"
                              class="text-sm font-medium text-gray-900 px-4 py-2 truncate overflow-hidden border-t border-r text-right bg-base-white"
                            >
                              {{ data.totals.fees[index] / 100 | currency: 'USD' : 'symbol' : '1.2-2' }}
                            </div>

                            <div
                              class="text-sm font-medium text-gray-900 px-4 py-2 truncate overflow-hidden border-t border-r text-right bg-base-white"
                            >
                              {{ data.totals.cost / 100 | currency: 'USD' : 'symbol' : '1.2-2' }}
                            </div>
                          </ng-container>

                          <div
                            class="sticky right-0 z-10 border-l text-sm font-medium text-gray-900 px-4 py-2 truncate overflow-hidden border-t border-r text-right bg-base-white"
                          >
                            {{ data.totals.retail / 100 | currency: 'USD' : 'symbol' : '1.2-2' }}
                            <!-- TODO: Multi-picker: Base, Add-Ons, Not Included -->
                          </div>
                        </ng-container>
                      </div>
                    </div>
                  </div>
                </ng-container>
              </vs-card>
            </ng-container>

            <ws-contract-ledger-card class="w-full" [warranty]="warranty"></ws-contract-ledger-card>
          </div>
        </vs-tab>
        <vs-tab *ngIf="canViewPayments$ | async" [config]="{ label: 'Payments' }">
          <ws-warranty-payments [warranty]="warranty" (refresh)="refresh(warranty.id)"></ws-warranty-payments>
        </vs-tab>
      </vs-tabs>

      <vs-dialog
        #termStartReasonDialog
        [config]="{ dialogContainerClass: 'max-w-[540px]', disableBackdropDismiss: termStartReasonSaving }"
        (closed)="handleTermStartDialogClosed()"
      >
        <vs-dialog-header
          [config]="{
            mode: 'simple',
            icon: 'info-circle',
            iconTheme: 'primary',
            title: 'Add Adjustment Reason',
            subtitle: 'Please provide a reason for changing the warranty start date. This is required for auditing.',
            hideCloseButton: true,
          }"
        >
        </vs-dialog-header>

        <div class="flex flex-col gap-4">
          <div
            *ngIf="termStartAuditContext as ctx"
            class="bg-gray-50 border border-gray-200 rounded-xl p-3 text-sm text-gray-800"
          >
            <div class="flex items-start justify-between gap-4">
              <div>
                <p class="font-medium">Previous start date</p>
                <p>{{ ctx.previousTermStart ? (ctx.previousTermStart | date: 'mediumDate') : 'N/A' }}</p>
              </div>
              <div class="text-right">
                <p class="font-medium">Updated start date</p>
                <p>{{ ctx.updatedTermStart | date: 'mediumDate' }}</p>
              </div>
            </div>
          </div>

          <vs-input-group>
            <label vsLabel for="term-start-reason">Reason<span class="text-danger">*</span>&nbsp;</label>
            <textarea
              vsInput
              id="term-start-reason"
              rows="4"
              [formControl]="termStartReasonControl"
              placeholder="Explain why the start date was changed"
            ></textarea>
            <p
              *ngIf="termStartReasonControl.invalid && termStartReasonControl.touched"
              class="text-sm text-danger mt-1 block"
            >
              Reason is required
            </p>
          </vs-input-group>
        </div>

        <div slot="buttons" class="flex gap-2 mt-2">
          <button
            vs-button
            class="flex-grow"
            theme="primary"
            [disabled]="termStartReasonSaving"
            (click)="submitTermStartReason()"
          >
            <ng-container *ngIf="!termStartReasonSaving">Submit</ng-container>
            <ng-container *ngIf="termStartReasonSaving">Saving...</ng-container>
          </button>
        </div>
      </vs-dialog>

      <vs-dialog #registerDialog [config]="{ dialogContainerClass: 'max-w-[500px]' }">
        <vs-dialog-header
          [config]="{
            mode: 'simple',

            icon: 'info-circle',
            iconTheme: 'primary',

            title: 'Finalize Warranty',
            subtitle:
              'Are you sure you want to finalize this warranty <strong>#' +
              warranty.contractNumber +
              '</strong>? This action cannot be undone. The customer will be notified via email and able to access the customer portal.',
          }"
        >
        </vs-dialog-header>

        <div slot="buttons" class="flex gap-2">
          <button vs-button appearance="outline" class="flex-grow" (click)="registerDialog.close()">Cancel</button>
          <button vs-button theme="primary" class="flex-grow" (click)="register(warranty); registerDialog.close()">
            Finalize
          </button>
        </div>
      </vs-dialog>

      <vs-dialog #deleteDialog [config]="{ dialogContainerClass: 'max-w-[500px]' }">
        <vs-dialog-header
          [config]="{
            mode: 'simple',

            icon: 'alert-circle',
            iconTheme: 'danger',

            title: 'Delete Warranty',
            subtitle:
              'Are you sure you want to delete this warranty <strong>#' +
              warranty.contractNumber +
              '</strong>? This action cannot be undone.',
          }"
        >
        </vs-dialog-header>

        <div slot="buttons" class="flex gap-2">
          <button vs-button appearance="outline" class="flex-grow" (click)="deleteDialog.close()">Cancel</button>
          <button vs-button theme="danger" class="flex-grow" (click)="delete(warranty); deleteDialog.close()">
            Delete
          </button>
        </div>
      </vs-dialog>
    </vs-app-page>
  </ng-container>
</ng-container>
